// Authenticated application shell: skip link, sidebar (desktop) / sheet
// (mobile), top bar with workspace switcher, command search and user menu.
import { useCompany } from "@/features/commercial/queries";
import { commercialNav } from "@/features/commercial/labels";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronsUpDown,
  LogOut,
  Menu,
  Monitor,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
  Moon,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  ShieldOff,
  Sun,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { api, call, csrf } from "@/api/client";
import { keys, useFrontDesk, useMe, useMemberships } from "@/api/hooks";
import type { Membership } from "@/api/types";
import { ROLE_LABELS } from "@/lib/labels";
import { Button } from "@/ui/button";
import { cn } from "@/ui/cn";
import { Dialog, SheetContent } from "@/ui/dialog";
import { Kbd } from "@/ui/layout";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/menu";
import { CommandMenu } from "./command-menu";
import { navigation } from "./nav";
import { readTheme, saveTheme, type ThemePreference } from "./theme";

export function Brand() {
  return (
    <span className="ia-official-logo">
      <img src="/brand/interactads-logo-light.svg" alt="InteractAds" />
    </span>
  );
}

export function StudioNavigation({ orgId }: { orgId: string }) {
  const { data: memberships = [] } = useMemberships();
  const [open, setOpen] = useState(false);
  return (
    <>
      <header className="ia-studio-navigation">
        <Link to={`/o/${orgId}`} aria-label="InteractAds · Inicio">
          <Brand />
        </Link>
        <nav className="ia-studio-primary-nav" aria-label="Navegación principal del Studio">
          <Link to={`/o/${orgId}/creatives`}>Creatividades</Link>
          <span className="ia-studio-nav-current" aria-current="page">
            Creative Studio
          </span>
          <Link to={`/o/${orgId}/campaigns`}>Campañas</Link>
        </nav>
        <div className="ia-studio-account">
          <WorkspaceSwitcher orgId={orgId} memberships={memberships} />
          <UserMenu orgId={orgId} />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Abrir navegación"
            onClick={() => {
              setOpen(true);
            }}
          >
            <Menu />
          </Button>
        </div>
      </header>
      <Dialog open={open} onOpenChange={setOpen}>
        <SheetContent title="Navegación de InteractAds">
          <Brand />
          <SideNav
            availableOnly
            orgId={orgId}
            onNavigate={() => {
              setOpen(false);
            }}
          />
        </SheetContent>
      </Dialog>
    </>
  );
}

/** Same platform destinations and capability gates as the shell; no invented modules. */
export function StudioSidebar({ orgId, children }: { orgId: string; children?: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <aside className="ia-studio-sidebar" data-collapsed={collapsed} aria-label="Navegación de la plataforma">
      <div className="ia-sidebar-collapse">
        <span>Espacio creativo</span>
        <Button
          variant="ghost"
          size="icon"
          aria-label={collapsed ? "Expandir menú lateral" : "Contraer menú lateral"}
          aria-expanded={!collapsed}
          onClick={() => {
            setCollapsed(!collapsed);
          }}
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </Button>
      </div>
      <SideNav orgId={orgId} availableOnly />
      <div className="ia-sidebar-current" aria-current="page">
        <Sparkles />
        <span>Creative Studio</span>
      </div>
      <div className="ia-sidebar-consumption">{children}</div>
    </aside>
  );
}

function SideNav({
  orgId,
  onNavigate,
  availableOnly = false,
}: {
  orgId: string;
  onNavigate?: () => void;
  availableOnly?: boolean;
}) {
  const { data: fd } = useFrontDesk(orgId);
  const { data: company } = useCompany(orgId);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const groups = navigation(orgId, fd, commercialNav(company));
  const isActive = (to: string) => (to === `/o/${orgId}` ? pathname === to : pathname.startsWith(to));
  return (
    <nav aria-label="Principal" className="grid gap-4 px-2 py-3">
      {fd?.capabilities.createCreative ? (
        <Button asChild variant="primary" className="mx-1 justify-start">
          <Link to={`/o/${orgId}/create`} onClick={onNavigate}>
            <Plus /> Crear creativo
          </Link>
        </Button>
      ) : null}
      {groups.map((group, index) => (
        <div key={group.label ?? `g${String(index)}`} className="grid gap-0.5">
          {group.label ? (
            <p className="px-2 pb-1 text-2xs font-medium uppercase tracking-wide text-subtle">{group.label}</p>
          ) : null}
          {group.items
            .filter((item) => !availableOnly || !item.soon)
            .map((item) => {
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  activeOptions={{ exact: item.to === `/o/${orgId}` }}
                  aria-label={item.label}
                  title={item.label}
                  onClick={onNavigate}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted hover:bg-sunken hover:text-fg [&_svg]:size-4",
                    active && "bg-sunken font-medium text-fg"
                  )}
                >
                  <item.icon aria-hidden="true" />
                  <span className="flex-1">{item.label}</span>
                  {item.soon ? <span className="text-2xs text-subtle">Pronto</span> : null}
                </Link>
              );
            })}
        </div>
      ))}
    </nav>
  );
}

function WorkspaceSwitcher({ orgId, memberships }: { orgId: string; memberships: Membership[] }) {
  const current = memberships.find((m) => m.organizationId === orgId);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="max-w-64 justify-between gap-2" aria-label="Cambiar espacio de trabajo">
          <span className="grid min-w-0 text-left leading-tight">
            <span className="truncate text-sm font-medium">{current?.organizationName ?? "Espacio de trabajo"}</span>
            {current ? <span className="truncate text-2xs text-muted">{ROLE_LABELS[current.role]}</span> : null}
          </span>
          <ChevronsUpDown className="text-muted" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Espacios de trabajo</DropdownMenuLabel>
        {memberships.map((m) => (
          <DropdownMenuItem key={m.organizationId} asChild>
            <Link to={`/o/${m.organizationId}`}>
              <span className="flex-1 truncate">{m.organizationName}</span>
              {m.organizationId === orgId ? <Check aria-label="Actual" /> : null}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/onboarding">
            <Plus /> Crear espacio de trabajo
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Operators of Media Interactiva (the server says so: /me.operatorRoles) get a separate way to the Administrador
 * Master, outside the organization's navigation. Every admin action is still re-authorized by the API and the
 * database and asks for the second factor (step-up); this link grants nothing.
 */
function PlatformAdminLink() {
  const { data: me } = useMe();
  if (!me?.operatorRoles.length) return null;
  return (
    <Button asChild variant="secondary" size="sm" className="gap-1.5 border-brand/40 text-brand">
      <Link
        to="/admin"
        aria-label="Administrador Master de la plataforma InteractAds"
        data-testid="platform-admin-link"
      >
        <ShieldCheck /> <span className="hidden md:inline">Administrador Master</span>
      </Link>
    </Button>
  );
}

function UserMenu({ orgId }: { orgId: string }) {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [theme, setTheme] = useState<ThemePreference>(readTheme);
  const logout = async (everywhere: boolean) => {
    try {
      if (everywhere) await call(() => api.POST("/auth/logout-all"));
      else await call(() => api.POST("/auth/logout"));
    } catch {
      // The session may already be gone; signing out locally is still correct.
    }
    csrf.set(null);
    queryClient.setQueryData(keys.me, null);
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    toast.success(everywhere ? "Cerraste sesión en todos tus dispositivos." : "Cerraste sesión.");
    await navigate({ to: "/login", search: { fresh: true } });
  };
  const setThemePreference = (value: ThemePreference) => {
    saveTheme(value);
    setTheme(value);
  };
  const initial = (me?.user.displayName || me?.user.email || "?").charAt(0).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Menú de usuario">
          <span className="grid size-7 place-items-center rounded-full bg-brand-soft text-xs font-semibold text-brand">
            {initial}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <span className="block truncate font-medium text-fg">{me?.user.displayName}</span>
          <span className="block truncate">{me?.user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Tu organización</DropdownMenuLabel>
        <DropdownMenuItem asChild>
          <Link to={`/o/${orgId}/settings`}>
            <Settings /> Configuración
          </Link>
        </DropdownMenuItem>
        {me?.operatorRoles.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Plataforma InteractAds</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link to="/admin">
                <ShieldCheck /> Administrador Master
              </Link>
            </DropdownMenuItem>
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Tema</DropdownMenuLabel>
        {(
          [
            ["light", "Claro", Sun],
            ["dark", "Oscuro", Moon],
            ["system", "Sistema", Monitor],
          ] as const
        ).map(([value, label, Icon]) => (
          <DropdownMenuItem
            key={value}
            onSelect={() => {
              setThemePreference(value);
            }}
          >
            <Icon /> <span className="flex-1">{label}</span>
            {theme === value ? <Check aria-label="Seleccionado" /> : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logout(false)}>
          <LogOut /> Cerrar sesión
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void logout(true)}>
          <ShieldOff /> Cerrar sesión en todos los dispositivos
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppShell({ orgId, children }: { orgId: string; children: ReactNode }) {
  const { data: memberships = [] } = useMemberships();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div className="flex min-h-dvh">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-brand px-3 py-2 text-brand-contrast focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
      >
        Saltar al contenido
      </a>
      <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-surface lg:flex">
        <div className="flex h-12 items-center border-b border-line px-4">
          <Brand />
        </div>
        <SideNav orgId={orgId} />
      </aside>
      <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent title="Navegación">
          <div className="flex h-12 items-center border-b border-line px-4">
            <Brand />
          </div>
          <SideNav
            orgId={orgId}
            onNavigate={() => {
              setMobileOpen(false);
            }}
          />
        </SheetContent>
      </Dialog>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-12 items-center gap-2 border-b border-line bg-surface/95 px-3 backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label="Abrir navegación"
            onClick={() => {
              setMobileOpen(true);
            }}
          >
            <Menu />
          </Button>
          <WorkspaceSwitcher orgId={orgId} memberships={memberships} />
          <div className="flex-1" />
          <Button
            variant="secondary"
            size="sm"
            className="gap-2 text-muted"
            onClick={() => {
              setCommandOpen(true);
            }}
          >
            <Search /> <span className="hidden sm:inline">Buscar o ir a…</span>
            <Kbd>⌘K</Kbd>
          </Button>
          <PlatformAdminLink />
          <UserMenu orgId={orgId} />
        </header>
        <main id="main" tabIndex={-1} className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 focus:outline-none sm:px-6">
          {children}
        </main>
      </div>
      <CommandMenu orgId={orgId} open={commandOpen} onOpenChange={setCommandOpen} />
    </div>
  );
}
